import React from "react";
import { connect } from "react-redux";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Badge as BadgeUI, Progress } from "reactstrap";
import NotificationAlert from "react-notification-alert";
import classnames from "classnames";
import moment from "moment";
import api from "./../../../Api.js";
import SimpleHeader from "./../../../components/Headers/SimpleHeader.jsx";
import HpBar from "./../rpg/HpBar.jsx";
import LevelBadge from "./LevelBadge.jsx";
import { formatXp, getStreakColor } from "./../../../helper/xpHelpers.js";

var PAGE_SIZE = 20;

// Traducoes de reasons comuns de XP
var XP_REASON_LABELS = {
  lesson_complete: 'Lição concluída',
  quiz_perfect: 'Quiz perfeito!',
  quiz_good: 'Quiz muito bom',
  unit_complete: 'Unidade completa',
  course_complete: 'CURSO COMPLETO!',
  streak_day: 'Bônus de sequência',
  mission_complete: 'Missão cumprida',
  enemy_defeated_common: 'Inimigo derrotado',
  enemy_defeated_elite: 'Elite derrotado',
  enemy_defeated_boss: 'BOSS DERROTADO!',
  event_double_xp: 'XP em dobro!',
  event_chest: 'Baú aberto',
  event_lightning_question: 'Pergunta relâmpago',
  manual_award: 'Bônus do professor',
  certificate_issued: 'Certificado emitido'
};

var GOLD_EXTRA_LABELS = {
  earn: 'ganho',
  spend: 'gasto',
  penalty: 'penalidade',
  purchase: 'compra',
  refund: 'reembolso',
  event: 'evento'
};

var HP_EXTRA_LABELS = {
  damage: 'Dano sofrido',
  heal: 'Cura recebida',
  revive: 'Ressuscitou!',
  reset: 'HP restaurado'
};

/**
 * ProgressHistory — "Diário do Herói": histórico de progresso da gamificação.
 * Cards de resumo (level, XP, gold, HP, streak, contadores) + timeline de eventos com filtros e paginação.
 */
class ProgressHistory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      summary: null,
      loadingSummary: true,
      events: [],
      loadingEvents: true,
      loadingMore: false,
      hasMore: false,
      offset: 0,
      kind: '' // '' = tudo | 'xp' | 'gold' | 'hp' | 'mission' | 'item'
    };
  }

  async componentDidMount() {
    await Promise.all([this.loadSummary(), this.loadHistory(true)]);
  }

  notify(type, title, message) {
    try {
      this.refs.notificationAlert.notificationAlert({
        place: 'tr',
        message: <div className="alert-text"><span className="alert-title">{title}</span><span> {message}</span></div>,
        type: type, icon: 'ni ni-bell-55', autoDismiss: 5
      });
    } catch (e) {}
  }

  async loadSummary() {
    if (!this.props.user || !this.props.user.id) return;
    try {
      var res = await api.getGamificationSummary(this.props.user.id);
      this.setState({ summary: res.data || null, loadingSummary: false });
    } catch (e) {
      this.setState({ loadingSummary: false });
      this.notify('danger', 'Ops!', 'Não consegui carregar o resumo da sua jornada. Tenta de novo daqui a pouco!');
    }
  }

  async loadHistory(reset) {
    if (!this.props.user || !this.props.user.id) return;
    var offset = reset ? 0 : this.state.offset;
    if (reset) {
      this.setState({ loadingEvents: true });
    } else {
      this.setState({ loadingMore: true });
    }

    try {
      var params = { limit: PAGE_SIZE, offset: offset };
      if (this.state.kind) params.kind = this.state.kind;
      var res = await api.getGamificationHistory(this.props.user.id, params);
      var data = res.data || {};
      var newEvents = data.events || [];
      this.setState({
        events: reset ? newEvents : this.state.events.concat(newEvents),
        hasMore: !!data.hasMore,
        offset: offset + newEvents.length,
        loadingEvents: false,
        loadingMore: false
      });
    } catch (e) {
      this.setState({ loadingEvents: false, loadingMore: false });
      this.notify('danger', 'Ops!', 'Não consegui carregar o diário. Tenta de novo daqui a pouco!');
    }
  }

  setFilter(kind) {
    if (kind === this.state.kind) return;
    this.setState({ kind: kind, offset: 0, events: [], hasMore: false }, () => {
      this.loadHistory(true);
    });
  }

  rarityColor(r) {
    var c = { common: '#9E9E9E', rare: '#2196F3', epic: '#9C27B0', legendary: '#FF9800', mythic: '#F44336' };
    return c[r] || '#9E9E9E';
  }

  rarityLabel(r) {
    var l = { common: 'comum', rare: 'raro', epic: 'épico', legendary: 'lendário', mythic: 'mítico' };
    return l[r] || r;
  }

  // Retorna { icon, color, title, detail, amountText, amountColor, highlight } pro evento
  describeEvent(e) {
    var amount = parseInt(e.amount) || 0;
    var signed = (amount >= 0 ? '+' : '') + amount;

    if (e.kind === 'xp') {
      return {
        icon: 'fas fa-bolt',
        color: '#FFC107',
        title: XP_REASON_LABELS[e.label] || e.label || 'XP ganho',
        detail: null,
        amountText: signed + ' XP',
        amountColor: '#FF9800',
        highlight: e.label === 'course_complete' || e.label === 'enemy_defeated_boss'
      };
    }

    if (e.kind === 'gold') {
      var extraLabel = GOLD_EXTRA_LABELS[e.extra] || e.extra;
      return {
        icon: 'fas fa-coins',
        color: '#FFD700',
        title: e.label || 'Movimentação de gold',
        detail: extraLabel ? String(extraLabel) : null,
        amountText: signed + ' gold',
        amountColor: amount >= 0 ? '#28a745' : '#dc3545',
        highlight: false
      };
    }

    if (e.kind === 'hp') {
      var isRevive = e.extra === 'revive';
      return {
        icon: 'fas fa-heart',
        color: isRevive ? '#9C27B0' : (amount >= 0 ? '#28a745' : '#dc3545'),
        title: HP_EXTRA_LABELS[e.extra] || e.label || 'Vida',
        detail: isRevive ? null : (e.label && e.label !== e.extra ? e.label : null),
        amountText: signed + ' HP',
        amountColor: amount >= 0 ? '#28a745' : '#dc3545',
        highlight: isRevive
      };
    }

    if (e.kind === 'mission') {
      return {
        icon: 'fas fa-flag',
        color: '#5e72e4',
        title: e.label || 'Missão cumprida',
        detail: 'Missão',
        amountText: '+' + Math.abs(amount) + ' XP',
        amountColor: '#FF9800',
        highlight: false
      };
    }

    if (e.kind === 'item') {
      return {
        icon: 'fas fa-gift',
        color: this.rarityColor(e.extra),
        title: 'Item obtido: ' + (e.label || 'item misterioso'),
        detail: null,
        amountText: null,
        amountColor: null,
        highlight: false,
        rarity: e.extra
      };
    }

    return {
      icon: 'fas fa-star',
      color: '#9E9E9E',
      title: e.label || 'Evento',
      detail: null,
      amountText: null,
      amountColor: null,
      highlight: false
    };
  }

  renderEvent(e, idx, isLast) {
    var info = this.describeEvent(e);
    //backend manda o id numerico da turma (ou null)
    var classroomName = e.classroom ? (typeof e.classroom === 'object' ? e.classroom.name : 'Turma #' + e.classroom) : null;

    return (
      <div
        key={'ev-' + idx}
        className="d-flex align-items-center py-3 px-2"
        style={{
          borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
          background: info.highlight ? '#FFF8E1' : 'transparent',
          borderRadius: info.highlight ? '8px' : '0'
        }}
      >
        {/* Icone do tipo */}
        <div style={{
          width: '42px', height: '42px', minWidth: '42px',
          borderRadius: '50%',
          background: info.color + '22',
          border: '2px solid ' + info.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <i className={info.icon} style={{ color: info.color, fontSize: '1rem' }} />
        </div>

        {/* Conteudo */}
        <div className="flex-grow-1 ml-3" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: info.highlight ? 800 : 600, fontSize: '0.9rem' }}>
            {info.title}
            {info.highlight && <span className="ml-1">✨</span>}
            {e.kind === 'item' && info.rarity && (
              <BadgeUI pill className="ml-2" style={{ background: this.rarityColor(info.rarity), color: '#fff', fontSize: '0.6rem' }}>
                {this.rarityLabel(info.rarity)}
              </BadgeUI>
            )}
          </div>
          <small className="text-muted">
            {moment(e.eventDate).format('DD/MM/YYYY HH:mm')}
            {info.detail && <span> · {info.detail}</span>}
            {classroomName && <span> · <i className="fas fa-users mr-1" />{classroomName}</span>}
          </small>
        </div>

        {/* Valor */}
        {info.amountText && (
          <div className="text-right ml-2">
            <strong style={{ color: info.amountColor, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
              {info.amountText}
            </strong>
          </div>
        )}
      </div>
    );
  }

  renderSummaryCards() {
    if (this.state.loadingSummary) {
      return (
        <Card className="shadow mb-4">
          <CardBody className="text-center py-4"><i className="fas fa-spinner fa-spin fa-2x text-muted" /></CardBody>
        </Card>
      );
    }

    var s = this.state.summary || {};
    var xp = s.xp || null;
    var gold = s.gold || null;
    var hp = s.hp || null;
    var streak = s.streak || null;
    var counters = s.counters || {};
    var level = (xp && xp.currentLevel) || null;
    var percent = xp ? Math.max(0, Math.min(100, Math.round(xp.percentToNext || 0))) : 0;

    var cardStyle = { minHeight: '120px' };

    return (
      <Row>
        {/* Level atual */}
        <Col xl="2" lg="4" md="4" sm="6" xs="6" className="mb-3">
          <Card className="shadow h-100" style={cardStyle}>
            <CardBody className="text-center py-3">
              <LevelBadge
                level={level ? level.level : 1}
                name={level ? level.name : 'Aventureiro'}
                icon={level ? level.icon : 'fas fa-medal'}
                size={48}
              />
              <div className="mt-2" style={{ fontWeight: 700, fontSize: '0.85rem', color: (level && level.color) || '#525f7f' }}>
                {level ? level.name : 'Aventureiro'}
              </div>
              <small className="text-muted">Nível {level ? level.level : 1}</small>
            </CardBody>
          </Card>
        </Col>

        {/* XP total */}
        <Col xl="3" lg="4" md="4" sm="6" xs="6" className="mb-3">
          <Card className="shadow h-100" style={cardStyle}>
            <CardBody className="py-3">
              <small className="text-muted text-uppercase" style={{ fontWeight: 700 }}>
                <i className="fas fa-bolt mr-1" style={{ color: '#FFC107' }} />XP Total
              </small>
              <h3 className="mb-1" style={{ color: '#FF9800' }}>{xp ? formatXp(xp.totalXp) : '0'} XP</h3>
              <Progress value={percent} style={{ height: '8px', backgroundColor: '#f0f0f0' }} color="warning" />
              <small className="text-muted">
                {xp && xp.xpToNextLevel > 0
                  ? ('Faltam ' + formatXp(xp.xpToNextLevel) + ' XP pro próximo nível (' + percent + '%)')
                  : 'Comece a ganhar XP completando aulas!'}
              </small>
            </CardBody>
          </Card>
        </Col>

        {/* Gold */}
        <Col xl="2" lg="4" md="4" sm="6" xs="6" className="mb-3">
          <Card className="shadow h-100" style={cardStyle}>
            <CardBody className="text-center py-3">
              <small className="text-muted text-uppercase" style={{ fontWeight: 700 }}>
                <i className="fas fa-coins mr-1" style={{ color: '#FFD700' }} />Gold
              </small>
              <h3 className="mb-0" style={{ color: '#DAA520' }}>{gold ? gold.balance : 0}</h3>
              <small className="text-muted">
                ganhou {gold ? gold.totalEarned : 0} · gastou {gold ? gold.totalSpent : 0}
              </small>
            </CardBody>
          </Card>
        </Col>

        {/* HP */}
        <Col xl="2" lg="4" md="4" sm="6" xs="6" className="mb-3">
          <Card className="shadow h-100" style={cardStyle}>
            <CardBody className="py-3 d-flex flex-column justify-content-center">
              <HpBar currentHp={hp ? hp.currentHp : 0} maxHp={hp ? hp.maxHp : 100} />
            </CardBody>
          </Card>
        </Col>

        {/* Streak */}
        <Col xl="3" lg="4" md="4" sm="6" xs="6" className="mb-3">
          <Card className="shadow h-100" style={cardStyle}>
            <CardBody className="text-center py-3">
              <small className="text-muted text-uppercase" style={{ fontWeight: 700 }}>Sequência</small>
              <h3 className="mb-0" style={{ color: getStreakColor(streak ? streak.currentStreak : 0) }}>
                <i className="fas fa-fire mr-2" />
                {streak ? streak.currentStreak : 0} {streak && streak.currentStreak === 1 ? 'dia' : 'dias'}
              </h3>
              <small className="text-muted">recorde: {streak ? streak.longestStreak : 0} dias 🔥</small>
            </CardBody>
          </Card>
        </Col>

        {/* Contadores */}
        <Col xs="12" className="mb-3">
          <Card className="shadow">
            <CardBody className="py-3">
              <Row className="text-center">
                <Col xs="4">
                  <i className="fas fa-flag mr-1" style={{ color: '#5e72e4' }} />
                  <strong>{counters.missionsCompleted || 0}</strong>
                  <small className="text-muted d-block">missões completas</small>
                </Col>
                <Col xs="4">
                  <i className="fas fa-gift mr-1" style={{ color: '#9C27B0' }} />
                  <strong>{counters.itemsOwned || 0}</strong>
                  <small className="text-muted d-block">itens no inventário</small>
                </Col>
                <Col xs="4">
                  <i className="fas fa-hand-holding-medical mr-1" style={{ color: '#28a745' }} />
                  <strong>{counters.revives || 0}</strong>
                  <small className="text-muted d-block">ressurreições</small>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  render() {
    var filters = [
      { key: '', label: 'Tudo', icon: 'fas fa-book-open' },
      { key: 'xp', label: 'XP', icon: 'fas fa-bolt' },
      { key: 'gold', label: 'Gold', icon: 'fas fa-coins' },
      { key: 'hp', label: 'Vida', icon: 'fas fa-heart' },
      { key: 'mission', label: 'Missões', icon: 'fas fa-flag' },
      { key: 'item', label: 'Itens', icon: 'fas fa-gift' }
    ];

    return (
      <React.Fragment>
        <div className="rna-wrapper"><NotificationAlert ref="notificationAlert" /></div>
        {!this.props.embedded && <SimpleHeader name="📖 Diário do Herói" way={[{ name: "Diário do Herói", url: "/history" }]} />}

        <Container className={this.props.embedded ? "mt-3" : "mt--6"} fluid>
          {/* Cards de resumo */}
          {this.renderSummaryCards()}

          {/* Timeline */}
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">
                    <i className="fas fa-scroll mr-2" style={{ color: '#8B4513' }} />
                    Linha do Tempo
                  </h3>
                </Col>
              </Row>
              <div className="mt-3">
                {filters.map((f) => (
                  <Button
                    key={f.key || 'all'}
                    size="sm"
                    className={classnames('mb-1', { 'shadow-sm': this.state.kind === f.key })}
                    color={this.state.kind === f.key ? 'primary' : 'secondary'}
                    style={{ borderRadius: '20px' }}
                    onClick={() => this.setFilter(f.key)}
                  >
                    <i className={f.icon + ' mr-1'} /> {f.label}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardBody className="pt-0">
              {this.state.loadingEvents ? (
                <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-muted" /></div>
              ) : this.state.events.length === 0 ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: '3rem' }}>🗺️</div>
                  <h4 className="mt-2">Sua jornada está só começando!</h4>
                  <p className="text-muted">Complete aulas pra encher este diário. ⚔️</p>
                </div>
              ) : (
                <React.Fragment>
                  {this.state.events.map((e, idx) => this.renderEvent(e, idx, idx === this.state.events.length - 1))}

                  {this.state.hasMore && (
                    <div className="text-center mt-3">
                      <Button
                        color="primary"
                        outline
                        onClick={() => this.loadHistory(false)}
                        disabled={this.state.loadingMore}
                      >
                        {this.state.loadingMore
                          ? <span><i className="fas fa-spinner fa-spin mr-2" />Carregando...</span>
                          : <span><i className="fas fa-chevron-down mr-2" />Carregar mais</span>}
                      </Button>
                    </div>
                  )}
                </React.Fragment>
              )}
            </CardBody>
          </Card>
        </Container>
      </React.Fragment>
    );
  }
}

export default connect(state => ({ user: state.user }))(ProgressHistory);
